import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { C4ModelSistemasService } from '../../../../../shared/services/api/c4-model/SistemasService';
import { ListaAcessoContainer } from './acesso-container/ListaAcessoContainer';
import { ContainerService } from '../../../../../shared/services/api/c4-model/ContainerService';
import { ListaAcessoComponentes } from './acesso-container/acesso-componentes/ListaAcessoComponentes';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (values: any) => void;
  currentSystemId?: string; // ID do sistema atual para omitir das opções
  initialValues?: {
    id?: string;
    sistema?: string;
    sistemaId?: string; // owner system id (may be undefined)
    sistemaIntegracaoId?: string; // id of the integrated system
    tipo?: string;
    tipoIntegracao?: string;
    descricao?: string;
    acessoContainers?: any[];
    acessoComponentes?: Array<{
      id?: string;
      nome: string;
    }>;
  };
};

export const ModalIntegracoes: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  currentSystemId,
  initialValues,
}) => {
  const [sistemaId, setSistemaId] = useState('');
  const [tipo, setTipo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [acessoContainers, setAcessoContainers] = useState<any[]>([]);
  const [acessoComponentes, setAcessoComponentes] = useState<
    Array<{
      id?: string;
      nome: string;
    }>
  >([]);
  const [isValid, setIsValid] = useState(false);
  const [sistemas, setSistemas] = useState<any[]>([]);
  const [loadingSistemas, setLoadingSistemas] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [tiposContainer, setTiposContainer] = useState<any[]>([]);
  const [loadingTiposContainer, setLoadingTiposContainer] = useState(false);
  // Carregar tipos de container apenas uma vez
  useEffect(() => {
    if (open && tiposContainer.length === 0 && !loadingTiposContainer) {
      setLoadingTiposContainer(true);
      ContainerService.getAll().then((result) => {
        if (result && !('message' in result)) {
          setTiposContainer(result);
        }
        setLoadingTiposContainer(false);
      });
    }
  }, [open]);

  const tiposIntegracao = ['SAAS', 'ONPREMISE'];

  // Carregar lista de sistemas quando o modal abrir
  useEffect(() => {
    if (open && sistemas.length === 0 && !loadingSistemas) {
      setLoadingSistemas(true);
      C4ModelSistemasService.getAll('1', 100, 0).then((result) => {
        if (result && !(result instanceof Error)) {
          // Filtrar o sistema atual das opções
          const filteredSistemas = result.data.filter(
            (sistema) => sistema.id !== currentSystemId
          );
          setSistemas(filteredSistemas);
        }
        setLoadingSistemas(false);
      });
    }
  }, [open, currentSystemId]);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        // initialValues may contain either tipo or tipoIntegracao
        setSistemaId(initialValues.sistemaIntegracaoId || initialValues.sistemaId || '');
        setTipo(initialValues.tipo || initialValues.tipoIntegracao || '');
        setDescricao(initialValues.descricao || '');
        setAcessoContainers(initialValues.acessoContainers || []);
        setAcessoComponentes(initialValues.acessoComponentes || []);
      } else {
        setSistemaId('');
        setTipo('');
        setDescricao('');
        setAcessoContainers([]);
        setAcessoComponentes([]);
      }
      setCurrentTab(0); // Reset to first tab when opening
    }
  }, [open, initialValues]);

  useEffect(() => {
    const allFilled = Boolean(
      sistemaId.trim() && tipo.trim() && descricao.trim()
    );
    setIsValid(allFilled);
  }, [sistemaId, tipo, descricao]);

  const handleSave = () => {
    const selectedSistema = sistemas.find((s) => s.id === sistemaId);

    // Estruturar os acessoContainers conforme esperado pelo backend
    const acessoContainersFormatted = acessoContainers.map((container) => ({
      // Keep optional ids if present (for edits)
      id: container.id,
      integracaoId: container.integracaoId,
      containerOrigem: container.containerOrigem,
      containerDestino: container.containerDestino,
      acao: container.acao,
      meioAcesso: container.meioAcesso,
      // Include acessoComponentes (either from the container or global modal list)
      acessoComponentes:
        (container.acessoComponentes && container.acessoComponentes.length > 0
          ? container.acessoComponentes.map((comp: any) => ({ nome: comp.nome }))
          : acessoComponentes.map((comp) => ({ nome: comp.nome }))),
    }));

    const values: any = {
      // Owner system (the system being edited/created)
      sistemaId: currentSystemId || initialValues?.sistemaId || '',
      // The integrated system id (target)
      sistemaIntegracaoId: sistemaId,
      // Keep a human-readable name for UI
      sistema: selectedSistema?.nome || '',
      // Tipo (compat)
      tipo: tipo,
      // Tipo no formato esperado pelo backend
      tipoIntegracao: tipo,
      descricao,
      // Optionally include the full sistemaIntegracao object if available
      sistemaIntegracao: selectedSistema || null,
      acessoContainers: acessoContainersFormatted,
      // also keep acessoComponentes for compatibility
      acessoComponentes,
    };


    if (initialValues && initialValues.id) values.id = initialValues.id;

    if (onSave) onSave(values);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // TabPanel component
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`integration-tabpanel-${index}`}
        aria-labelledby={`integration-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>Integração</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Basic Integration Info */}
          <Box>
            <FormControl fullWidth margin="normal">
              <InputLabel id="sistema-label">Sistema</InputLabel>
              <Select
                labelId="sistema-label"
                id="sistema-select"
                value={sistemaId}
                label="Sistema"
                onChange={(e) => setSistemaId(e.target.value as string)}
                disabled={loadingSistemas}
              >
                {sistemas.map((sistema) => (
                  <MenuItem key={sistema.id} value={sistema.id}>
                    {sistema.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="tipo-label">Tipo</InputLabel>
              <Select
                labelId="tipo-label"
                id="tipo-select"
                value={tipo}
                label="Tipo"
                onChange={(e) => setTipo(e.target.value as string)}
              >
                {tiposIntegracao.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Descrição"
              margin="normal"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Integração para sincronização de dados financeiros"
              multiline
              rows={3}
            />
          </Box>

          {/* Tabs Section */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="integration tabs"
            >
              <Tab label="Acesso a Containers" />
              <Tab label="Acesso a Componentes" />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={currentTab} index={0}>
            <ListaAcessoContainer
              initialRows={
                acessoContainers.length > 0
                  ? [
                    {
                      id: initialValues?.id || '',
                      sistemaId: '',
                      sistemaIntegracaoId: sistemaId,
                      tipoIntegracao: tipo,
                      descricao: descricao,
                      sistemaIntegracao: null,
                      acessoContainers: acessoContainers,
                    },
                  ]
                  : []
              }
              onAcessoContainersChange={setAcessoContainers}
              tiposContainer={tiposContainer}
              loadingTiposContainer={loadingTiposContainer}
            />
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <ListaAcessoComponentes
              initialData={acessoComponentes}
              onAcessoComponentesChange={setAcessoComponentes}
            />
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          disabled={!isValid}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
